import { useSyncExternalStore } from 'react'

export type UpdateServiceWorker = (reloadPage?: boolean) => Promise<void>

type PwaLifecycleState = {
  offlineReady: boolean
  offlinePromptVisible: boolean
  updateAvailable: boolean
  updatePromptVisible: boolean
  updateServiceWorker: UpdateServiceWorker | null
  updating: boolean
}

const initialState: PwaLifecycleState = {
  offlineReady: false,
  offlinePromptVisible: false,
  updateAvailable: false,
  updatePromptVisible: false,
  updateServiceWorker: null,
  updating: false,
}

let state = initialState
const listeners = new Set<() => void>()

function publish(next: Partial<PwaLifecycleState>): void {
  state = { ...state, ...next }
  for (const listener of listeners) listener()
}

export function subscribePwaLifecycle(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getPwaLifecycleSnapshot(): PwaLifecycleState {
  return state
}

export function usePwaLifecycle(): PwaLifecycleState {
  return useSyncExternalStore(subscribePwaLifecycle, getPwaLifecycleSnapshot, getPwaLifecycleSnapshot)
}

export function notifyOfflineReady(): void {
  publish({ offlineReady: true, offlinePromptVisible: true })
}

export function dismissOfflineReady(): void {
  publish({ offlinePromptVisible: false })
}

export function notifyPwaUpdateAvailable(updateServiceWorker: UpdateServiceWorker): void {
  publish({ updateAvailable: true, updatePromptVisible: true, updateServiceWorker })
}

export function dismissPwaUpdate(): void {
  publish({ updatePromptVisible: false })
}

export async function activatePwaUpdate(): Promise<void> {
  if (!state.updateServiceWorker || state.updating) return
  const updateServiceWorker = state.updateServiceWorker
  publish({ updating: true, updatePromptVisible: false })
  try {
    await updateServiceWorker(true)
    publish({ updateAvailable: false, updateServiceWorker: null })
  } finally {
    publish({ updating: false })
  }
}

export function resetPwaLifecycleForTests(): void {
  state = initialState
  for (const listener of listeners) listener()
}
